import Link from 'next/link';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { capitalizeString } from '@/utils/string/capitalizeString';
import { BASE } from '@/types/utils/sizesType';

// Types
import type { MediaViewerPostType } from '@/types/mediaViewer/MediaViewerPostType';
import type { SocialProfileNetworkType, SocialProfileType } from '@/types/socialProfileType';

import { INSTAGRAM, PINTEREST, YOUTUBE } from '@/lib/socialProfile';

// Libs
import { iconSize } from '@/components/base/Avatar/avatarConfig';
// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';

const SocialIcon = ({ socialProfileType }: { socialProfileType: SocialProfileNetworkType }) => {
    switch (socialProfileType) {
        case INSTAGRAM:
            return <Instagram aria-label="instagram" data-testid="instagram-icon" />;
        case PINTEREST:
            return <PinterestIcon aria-label="pinterest" />;
        case YOUTUBE:
            return <YoutubeIcon aria-label="youtube" />;
        default:
            return (
                <Icon
                    data-testid={`${socialProfileType}-icon`}
                    icon={socialProfileType}
                    className={clsx(`${iconSize[BASE]} dark:text-white`, {
                        'text-facebook': socialProfileType === 'facebook',
                        'text-linkedin': socialProfileType === 'linkedin',
                        'text-twitter': socialProfileType === 'twitter',
                        'text-black': socialProfileType === 'tiktok_business'
                    })}
                />
            );
    }
};

type MediaViewerViewPostOnNetworkProps = {
    socialProfile: SocialProfileType;
    mediaViewerPost: MediaViewerPostType;
};

export function MediaViewerViewPostOnNetwork({
    socialProfile,
    mediaViewerPost
}: MediaViewerViewPostOnNetworkProps) {
    const { t } = useTranslation('commons');

    return (
        <Tooltip
            content={
                <span className="inline-block" data-testid="tooltip-content">
                    {t('media_viewer.view_on', {
                        socialProfileType: capitalizeString(
                            socialProfile?.social_network_type === 'tiktok_business'
                                ? 'tiktok'
                                : socialProfile?.social_network_type
                        )
                    })}
                </span>
            }
            placement="top"
            appendTo="parent">
            <Link
                className="flex rounded-m bg-icoGray-100 p-2.5 dark:bg-icoDarkMode-deepBlack"
                href={mediaViewerPost?.permalink}
                target="_blank"
                rel="noopener noreferrer">
                <SocialIcon socialProfileType={socialProfile?.social_network_type} />
            </Link>
        </Tooltip>
    );
}
